/**
 * Client-side image preparation: downscale to at most 2048px on the long side and re-encode
 * as JPEG at quality 0.85 before upload. HEIC from the iPhone camera goes through the same
 * path (Safari decodes it into the canvas). Falls back to the original file when the browser
 * cannot decode it.
 */
export const MAX_IMAGE_SIDE = 2048;
export const JPEG_QUALITY = 0.85;

export async function prepareImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }
  try {
    const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    if (scale === 1 && file.type === 'image/jpeg' && file.size < 2 * 1024 * 1024) return file;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob) return file;
    const name = file.name.replace(/\.[a-z0-9]+$/iu, '') || 'photo';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
  } finally {
    bitmap.close();
  }
}

/** "1.2 MB" style size for file bubbles. */
export function formatBytes(bytes: number, locale = 'en'): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const n = new Intl.NumberFormat(locale.startsWith('ar') ? 'ar-u-nu-latn' : locale, {
    maximumFractionDigits: v < 10 && i > 0 ? 1 : 0,
  }).format(v);
  return `${n} ${units[i]}`;
}

/** Any video/* file (by type, or by extension when the browser gives no type). */
export function isVideoFile(file: { type: string; name: string }): boolean {
  return file.type.startsWith('video/') || /\.(mp4|mov|m4v|webm|mkv|avi|3gp)$/iu.test(file.name);
}
